import { useState, useEffect, useCallback, useContext } from "react";
import { App, Button, Card, Flex, Typography } from "antd";

import Breadcrumbs from "@/components/ui/Breadcrumbs";
import PlusIcon from "@/icons/PlusIcon";
import { UserContext } from "@/context/UserContext";

import NSPDKeyCategoryModal from "@/components/modals/NSPDKeyCategoryModal";
import NSPDKeyCategoriesTable from "@/components/tables/NSPDKeyCategoriesTable";
import NSPDKeyModal from "@/components/modals/NSPDKeyModal";
import NSPDKeyTable from "@/components/tables/NSPDKeyTable";
import NSPDRegistryJsonPreview from "./components/NSPDRegistryJsonPreview";

import {
  getKeyCategories,
  getKeyCategoriesOptions,
  deleteKeyCategory,
  getKeys,
  deleteKey,
} from "@/api/nspd";

const DEFAULT_PAGE_SIZE = 10;
const CATEGORY_PAGINATION_STORAGE_KEY = "nspdKeyCategoriesPagination";
const KEY_PAGINATION_STORAGE_KEY = "nspdKeysPagination";

const defaultPagination = {
  current: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  total: 0,
  showSizeChanger: true,
  placement: ["bottomLeft"],
};

const getStorageKey = (userUuid, key) =>
  userUuid ? `${userUuid}_${key}` : key;

const getInitialPagination = (userUuid, key) => {
  try {
    const saved = JSON.parse(localStorage.getItem(getStorageKey(userUuid, key)));

    return {
      ...defaultPagination,
      ...saved,
      total: 0,
    };
  } catch (error) {
    return defaultPagination;
  }
};

const normalizeSorter = (sorter) => {
  if (!sorter?.order) return [];

  return [
    {
      columnKey: sorter.columnKey || sorter.field,
      field: sorter.field,
      order: sorter.order,
    },
  ];
};

const getSortString = (sort = []) =>
  sort
    .map(({ columnKey, order }) =>
      order === "descend" ? `-${columnKey}` : columnKey,
    )
    .join(",");

// Страница настройки ключей НСПД
const NSPDSettings = () => {
  const { message } = App.useApp();
  const { user } = useContext(UserContext);
  const userUuid = user?.user_uuid;

  const [categories, setCategories] = useState([]);
  const [keyCategoryOptions, setKeyCategoryOptions] = useState([]);
  const [nspd_keys, setNSPDKeys] = useState([]);

  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);
  const [isKeysLoading, setIsKeysLoading] = useState(false);

  const [isCategoryModalVisible, setIsCategoryModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isKeyModalVisible, setIsKeyModalVisible] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);

  const [categoryPagination, setCategoryPagination] = useState(() =>
    getInitialPagination(userUuid, CATEGORY_PAGINATION_STORAGE_KEY),
  );

  const [keyPagination, setKeyPagination] = useState(() =>
    getInitialPagination(userUuid, KEY_PAGINATION_STORAGE_KEY),
  );

  const [categorySort, setCategorySort] = useState([
    {
      columnKey: "name",
      field: "name",
      order: "ascend",
    },
  ]);

  const [keySort, setKeySort] = useState([
    {
      columnKey: "name",
      field: "name",
      order: "ascend",
    },
  ]);

  const fetchCategories = useCallback(
    async (page = 1, pageSize = DEFAULT_PAGE_SIZE, sort = categorySort) => {
      try {
        setIsCategoriesLoading(true);
        const sortString = getSortString(sort);

        const response = await getKeyCategories({
          page,
          perpage: pageSize,
          ...(sortString && { sort: sortString }),
        });

        const items = response.data || [];
        const total = response.total || 0;
        const nextPagination = {
          current: page,
          pageSize,
          total,
        };

        setCategories(items);
        setCategoryPagination((prev) => ({
          ...prev,
          ...nextPagination,
        }));
        localStorage.setItem(
          getStorageKey(userUuid, CATEGORY_PAGINATION_STORAGE_KEY),
          JSON.stringify(nextPagination),
        );
      } catch (error) {
        message.error("Не удалось загрузить категории ключей");
      } finally {
        setIsCategoriesLoading(false);
      }
    },
    [message, categorySort, userUuid],
  );

  const fetchKeyCategoryOptions = useCallback(async () => {
    try {
      const items = await getKeyCategoriesOptions({
        sort: "name",
      });

      setKeyCategoryOptions(items);
    } catch (error) {
      message.error("Не удалось загрузить категории ключей");
    }
  }, [message]);

  const fetchKeys = useCallback(
    async (page = 1, pageSize = DEFAULT_PAGE_SIZE, sort = keySort) => {
      try {
        setIsKeysLoading(true);
        const sortString = getSortString(sort);

        const response = await getKeys({
          page,
          perpage: pageSize,
          ...(sortString && { sort: sortString }),
        });

        const items = response.data || [];
        const total = response.total || 0;
        const nextPagination = {
          current: page,
          pageSize,
          total,
        };

        setNSPDKeys(items);
        setKeyPagination((prev) => ({
          ...prev,
          ...nextPagination,
        }));
        localStorage.setItem(
          getStorageKey(userUuid, KEY_PAGINATION_STORAGE_KEY),
          JSON.stringify(nextPagination),
        );
      } catch (error) {
        message.error("Не удалось загрузить ключи");
      } finally {
        setIsKeysLoading(false);
      }
    },
    [message, keySort, userUuid],
  );

  useEffect(() => {
    fetchCategories(
      categoryPagination.current,
      categoryPagination.pageSize,
      categorySort,
    );
    fetchKeyCategoryOptions();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchKeys(keyPagination.current, keyPagination.pageSize, keySort);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateCategory = () => {
    setSelectedCategory(null);
    setIsCategoryModalVisible(true);
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setIsCategoryModalVisible(true);
  };

  const handleCloseCategoryModal = () => {
    setSelectedCategory(null);
    setIsCategoryModalVisible(false);
  };

  const handleDeleteCategory = async (category) => {
    if (category.is_used) {
      message.warning("Нельзя удалить категорию, которая используется");
      return;
    }

    try {
      await deleteKeyCategory({ uuid: category.uuid });
      message.success("Категория удалена");

      const isLastItemOnPage =
        categories.length === 1 && categoryPagination.current > 1;

      const nextPage = isLastItemOnPage
        ? categoryPagination.current - 1
        : categoryPagination.current;

      await fetchCategories(
        nextPage,
        categoryPagination.pageSize,
        categorySort,
      );
      await fetchKeyCategoryOptions();
    } catch (error) {
      message.error("Не удалось удалить категорию");
    }
  };

  const handleCreateKey = () => {
    setSelectedKey(null);
    setIsKeyModalVisible(true);
  };

  const handleEditKey = (key) => {
    setSelectedKey(key);
    setIsKeyModalVisible(true);
  };

  const handleCloseKeyModal = () => {
    setSelectedKey(null);
    setIsKeyModalVisible(false);
  };

  const handleDeleteKey = async (key) => {
    if (key.is_used) {
      message.warning("Нельзя удалить ключ, который используется");
      return;
    }

    try {
      await deleteKey({ uuid: key.uuid });
      message.success("Ключ удален");

      const isLastItemOnPage =
        nspd_keys.length === 1 && keyPagination.current > 1;

      const nextPage = isLastItemOnPage
        ? keyPagination.current - 1
        : keyPagination.current;

      await fetchKeys(nextPage, keyPagination.pageSize, keySort);
    } catch (error) {
      message.error("Не удалось удалить ключ");
    }
  };

  const handleCategoryTableChange = (
    nextPagination,
    filters,
    sorter,
    extra,
  ) => {
    const nextSort = normalizeSorter(sorter);
    const isSortChange = extra?.action === "sort";

    setCategorySort(nextSort);

    fetchCategories(
      isSortChange ? 1 : nextPagination.current,
      nextPagination.pageSize,
      nextSort,
    );
  };

  const handleKeyTableChange = (nextPagination, filters, sorter, extra) => {
    const nextSort = normalizeSorter(sorter);
    const isSortChange = extra?.action === "sort";

    setKeySort(nextSort);

    fetchKeys(
      isSortChange ? 1 : nextPagination.current,
      nextPagination.pageSize,
      nextSort,
    );
  };

  return (
    <Flex vertical gap={24}>
      <Flex vertical gap={10}>
        <Breadcrumbs
          items={[
            {
              title: "Административная панель",
            },
            {
              title: "Настройки ключей НСПД",
            },
          ]}
        />
        <Typography.Title level={2} className="no-margin">
          Настройки ключей НСПД
        </Typography.Title>
      </Flex>

      <NSPDRegistryJsonPreview />

      <Card>
        <Flex vertical gap={16}>
          <Flex justify="space-between" align="center">
            <Typography.Title level={4} style={{ margin: 0 }}>
              Категории ключей
            </Typography.Title>

            <Button
              type="primary"
              icon={<PlusIcon />}
              onClick={handleCreateCategory}
            />
          </Flex>

          <NSPDKeyCategoriesTable
            data={categories}
            loading={isCategoriesLoading}
            pagination={categoryPagination}
            sortedInfo={categorySort}
            onChange={handleCategoryTableChange}
            onEditItem={handleEditCategory}
            onDeleteItem={handleDeleteCategory}
          />
        </Flex>
      </Card>

      <Card>
        <Flex vertical gap={16}>
          <Flex justify="space-between" align="center">
            <Typography.Title level={4} style={{ margin: 0 }}>
              Ключи
            </Typography.Title>

            <Button
              type="primary"
              icon={<PlusIcon />}
              onClick={handleCreateKey}
            />
          </Flex>

          <NSPDKeyTable
            data={nspd_keys}
            loading={isKeysLoading}
            pagination={keyPagination}
            sortedInfo={keySort}
            onChange={handleKeyTableChange}
            onEditItem={handleEditKey}
            onDeleteItem={handleDeleteKey}
          />
        </Flex>
      </Card>

      <NSPDKeyCategoryModal
        visible={isCategoryModalVisible}
        category={selectedCategory}
        onCancel={handleCloseCategoryModal}
        onSuccess={() => {
          fetchCategories(
            categoryPagination.current,
            categoryPagination.pageSize,
            categorySort,
          );
          fetchKeyCategoryOptions();
        }}
      />

      <NSPDKeyModal
        visible={isKeyModalVisible}
        currentKey={selectedKey}
        categories={keyCategoryOptions}
        onCancel={handleCloseKeyModal}
        onSuccess={() => {
          fetchKeys(keyPagination.current, keyPagination.pageSize, keySort);
          fetchKeyCategoryOptions();
        }}
      />
    </Flex>
  );
};

export default NSPDSettings;
